import {
  BOOKING_FIELD_CHANGE,
  CHANGE_BOOKING_REDIRECT_URL,
  CHANGE_PAID_BOOKING_REDIRECT_URL,
  FIELD_CHANGE,
  FIELD_CHANGE_INVALID,
  FINISH_SURVEY,
  PAID_BOOKING_FIELD_CHANGE,
  SUBMIT_FAIL,
  SUBMIT_SUCCESS,
  TOGGLE_EXPLICIT_CONSENT_CONFIRM,
} from 'publisher/actionTypes'
import {
  OPT_IN_RECAPTCHA_VALIDATION_FAILED,
  SUBMIT_RECAPTCHA,
} from 'publisher/store/optIn/optInActionTypes'

export const customerCountryPlaceholder = '%CUSTOMER_COUNTRY%'
const nextStepUrlPlaceholder = '%NEXT_STEP_URL%'
const customerFieldsPlaceholder = '%CUSTOMER_FIELDS%'
const availableCountriesPlaceholder = '%AVAILABLE_COUNTRIES%'

export const defaultState = {
  customerFields: customerFieldsPlaceholder,
  fields: {},
  availableCountries: availableCountriesPlaceholder,
  errors: {
    common: [],
    fields: {},
    bookingFields: {},
    paidBookingFields: {},
  },
  bookingFields: {},
  paidBookingFields: {},
  success: false,
  submitted: false,
  customerCountry: customerCountryPlaceholder,
  nextStepUrl: nextStepUrlPlaceholder,
  surveyResults: null,
  surveysResults: undefined,
  submitedRecaptchas: [],
  optInRecaptchaError: {},
  bookingRedirectUrl: '',
  paidBookingRedirectUrl: '',
}

const updateSubmitedRecaptchas = (state, newRecaptcha) => {
  const foundRecaptcha = state.find(
    recaptcha => recaptcha.id === newRecaptcha.id,
  )
  if (foundRecaptcha) {
    return state.reduce((acc, curr) => {
      if (curr.id === newRecaptcha.id) {
        acc.push(newRecaptcha)
      } else acc.push(curr)
      return acc
    }, [])
  }

  return [...state, newRecaptcha]
}

export default function (state = defaultState, action) {
  const { type, payload } = action

  switch (type) {
    case SUBMIT_SUCCESS:
      return {
        ...state,
        fields: {
          ...Object.keys(state.fields).reduce(
            (prev, slug) => ({ ...prev, [slug]: '' }),
            {},
          ),
          country: state.fields.country, // avoid price plan flash (vat)
        },
        bookingFields: Object.keys(state.bookingFields ?? {}).reduce(
          (prev, slug) => {
            prev[slug] = ''
            return prev
          },
          {},
        ),
        paidBookingFields: Object.keys(state.paidBookingFields ?? {}).reduce(
          (prev, slug) => {
            prev[slug] = ''
            return prev
          },
          {},
        ),
        success: true,
        submitted: true,
      }
    case SUBMIT_FAIL:
      return {
        ...state,
        errors: {
          common: payload.common || [],
          fields: {
            ...payload.fields,
          },
          bookingFields: payload.bookingFields || {},
          paidBookingFields: payload.paidBookingFields || {},
        },
        success: false,
        submitted: true,
      }
    case FIELD_CHANGE: {
      const fields = { ...state.errors.fields }
      delete fields[payload.slug]

      return {
        ...state,
        fields: {
          ...state.fields,
          [payload.slug]: payload.value,
        },
        errors: {
          ...state.errors,
          common: [],
          fields,
        },
        success: false,
      }
    }
    case BOOKING_FIELD_CHANGE: {
      const bookingFields = { ...state.errors.bookingFields }
      delete bookingFields[payload.slug]

      return {
        ...state,
        bookingFields: {
          ...state.bookingFields,
          [payload.slug]: payload.value,
        },
        errors: {
          ...state.errors,
          common: [],
          bookingFields,
        },
        success: false,
      }
    }
    case PAID_BOOKING_FIELD_CHANGE: {
      const paidBookingFields = { ...state.errors.paidBookingFields }
      delete paidBookingFields[payload.slug]

      return {
        ...state,
        paidBookingFields: {
          ...state.paidBookingFields,
          [payload.slug]: payload.value,
        },
        errors: {
          ...state.errors,
          common: [],
          paidBookingFields,
        },
        success: false,
      }
    }
    case FIELD_CHANGE_INVALID:
      return {
        ...state,
        errors: {
          ...state.errors,
          fields: {
            ...state.fields,
            [payload.slug]: payload.value,
          },
        },
      }
    case TOGGLE_EXPLICIT_CONSENT_CONFIRM:
      return {
        ...state,
        errors: {
          ...state.errors,
          common: [],
        },
        submitted: false,
      }
    case FINISH_SURVEY:
      return {
        ...state,
        surveyResults: {
          ...state.surveyResults,
          ...payload,
        },
        surveysResults: {
          ...state.surveysResults,
          ...payload,
        },
      }
    case SUBMIT_RECAPTCHA: {
      return {
        ...state,
        submitedRecaptchas: updateSubmitedRecaptchas(
          state.submitedRecaptchas,
          payload,
        ),
        optInRecaptchaError:
          state.optInRecaptchaError.id === payload.id
            ? {}
            : state.optInRecaptchaError,
      }
    }
    case OPT_IN_RECAPTCHA_VALIDATION_FAILED:
      return {
        ...state,
        optInRecaptchaError: payload,
      }
    case CHANGE_BOOKING_REDIRECT_URL:
      return {
        ...state,
        bookingRedirectUrl: payload,
      }
    case CHANGE_PAID_BOOKING_REDIRECT_URL:
      return {
        ...state,
        paidBookingRedirectUrl: payload,
      }

    default:
      return state
  }
}

export const getFieldValue = (state, slug) =>
  state.fields[slug] ? state.fields[slug] : ''

export const getBookingFieldValue = (state, slug) =>
  state.bookingFields && state.bookingFields[slug] !== undefined
    ? state.bookingFields[slug]
    : ''

export const getPaidBookingFieldValue = (state, slug) =>
  state.paidBookingFields && state.paidBookingFields[slug] !== undefined
    ? state.paidBookingFields[slug]
    : ''

export const getCustomerFields = ({ customerFields }) => {
  try {
    return JSON.parse(customerFields)
  } catch (e) {
    return {}
  }
}

export const getCustomerFieldValue = (state, slug) =>
  getCustomerFields(state)[slug]

export const getFieldErrors = (state, slug) => state.errors.fields[slug] || []

const getBookingFieldErrors = (state, slug) =>
  state.errors.bookingFields[slug] || []

const getPaidBookingFieldErrors = (state, slug) =>
  state.errors.paidBookingFields[slug] || []

export const getErrors = state => state.errors

export const getCommonErrors = ({ errors }) => errors.common

export const getCustomerCountry = ({ customerCountry }) =>
  customerCountry !== customerCountryPlaceholder ? customerCountry : null

export const getAvailableCountries = ({ availableCountries }) => {
  try {
    return JSON.parse(availableCountries)
  } catch (e) {
    return {}
  }

  return availableCountries
}

export const isSubmitted = ({ submitted }) => submitted

export const isSubmitSuccessful = ({ success }) => success

export const getNextStepUrl = ({ nextStepUrl }) => nextStepUrl

export const getBookingRedirectUrl = ({ bookingRedirectUrl }) =>
  bookingRedirectUrl

export const getPaidBookingRedirectUrl = ({ bookingRedirectUrl }) =>
  bookingRedirectUrl

export const getFields = ({ fields }) => fields

export const getCustomerOptInFields = ({ fields }) => {
  const { datetime, timerDatetime, ...customerFields } = fields
  return customerFields
}

export const getBookingOptInFields = ({ bookingFields }) => {
  return bookingFields || {}
}

export const getPaidBookingOptInFields = ({ paidBookingFields }) => {
  return paidBookingFields || {}
}

export const getFieldsBySlugs = ({ fields }, slugs) =>
  slugs.reduce((acc, slug) => ({ ...acc, [slug]: fields[slug] }), {})

export const getDateTimeField = ({ fields }) =>
  fields.datetime || fields.timerDatetime // todo could it be one?

export const getSurveyResults = state => state.surveyResults

export const selectors = {
  getFieldErrors,
  getBookingFieldErrors,
  getPaidBookingFieldErrors,
  getFieldValue,
  getFields,
  getNextStepUrl,
  getCustomerFields,
  getCustomerCountry,
  getCustomerOptInFields,
  getDateTimeField,
  getSurveyResults,
  getAvailableCountries,
  getBookingOptInFields,
  getPaidBookingOptInFields,
  getBookingFieldValue,
  getPaidBookingFieldValue,
}
